const PERFORMANCE_ENDPOINT = "/api/v1/performance_metrics";
const RESOURCE_TYPE_GROUPS = {
  script: new Set(["script"]),
  css: new Set(["link", "css"]),
  img: new Set(["img", "image"]),
};

let initialized = false;
let navigationStartedAt = 0;
let navigationKind = "initial";
let latestLcpEntry = null;
let longTaskCount = 0;
let rumMetricsFlushed = false;

const sanitizeTags = (tags = {}) =>
  Object.entries(tags).reduce((sanitized, [key, value]) => {
    if (value === undefined || value === null || value === "") {
      return sanitized;
    }

    sanitized[key] =
      typeof value === "number" || typeof value === "boolean"
        ? value
        : String(value).slice(0, 255);
    return sanitized;
  }, {});

const metricPage = () =>
  typeof window === "undefined" ? "unknown" : window.location.pathname;

const monotonicNow = () =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

const sendPayload = (payload) => {
  const body = JSON.stringify(payload);

  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon(PERFORMANCE_ENDPOINT, blob)) {
      return;
    }
  }

  if (typeof fetch === "function") {
    fetch(PERFORMANCE_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body,
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => {});
  }
};

export const reportPerformanceMetric = (
  name,
  value,
  tags = {},
  category = "rum",
  metricType = "gauge",
) => {
  if (!name || typeof value !== "number" || Number.isNaN(value) || !Number.isFinite(value)) {
    return;
  }

  sendPayload({
    name,
    value: Number(value.toFixed(2)),
    category,
    metric_type: metricType,
    page: metricPage(),
    occurred_at: new Date().toISOString(),
    tags: sanitizeTags(tags),
  });
};

export const startMetricTimer = (name, tags = {}, category = "interaction") => {
  const startedAt = monotonicNow();

  return (extraTags = {}) => {
    reportPerformanceMetric(
      name,
      monotonicNow() - startedAt,
      { ...tags, ...extraTags },
      category,
    );
  };
};

const reportNavigationMetrics = () => {
  if (typeof performance === "undefined") {
    return;
  }

  const navigationEntry = performance.getEntriesByType("navigation")[0];
  if (!navigationEntry) {
    return;
  }

  reportPerformanceMetric("nav_ttfb_ms", navigationEntry.responseStart, {
    navigation_kind: navigationKind,
  });
  reportPerformanceMetric(
    "nav_dom_content_loaded_ms",
    navigationEntry.domContentLoadedEventEnd,
    { navigation_kind: navigationKind },
  );
  reportPerformanceMetric("nav_load_ms", navigationEntry.loadEventEnd, {
    navigation_kind: navigationKind,
  });
};

const reportResourceMetrics = () => {
  if (typeof performance === "undefined") {
    return;
  }

  const resources = performance.getEntriesByType("resource");

  Object.entries(RESOURCE_TYPE_GROUPS).forEach(([resourceType, initiatorTypes]) => {
    const matchingResources = resources.filter((entry) =>
      initiatorTypes.has(entry.initiatorType),
    );

    if (!matchingResources.length) {
      return;
    }

    const totalDuration = matchingResources.reduce(
      (sum, entry) => sum + entry.duration,
      0,
    );

    reportPerformanceMetric("resource_total_duration_ms", totalDuration, {
      navigation_kind: navigationKind,
      resource_type: resourceType,
      resource_count: matchingResources.length,
    });
  });
};

const flushRumMetrics = () => {
  if (rumMetricsFlushed) {
    return;
  }

  rumMetricsFlushed = true;
  reportNavigationMetrics();
  reportResourceMetrics();
  reportPerformanceMetric("long_task_count", longTaskCount, {
    navigation_kind: navigationKind,
  });

  if (latestLcpEntry) {
    reportPerformanceMetric("nav_lcp_ms", latestLcpEntry.startTime, {
      navigation_kind: navigationKind,
    });
  }
};

const registerObservers = () => {
  if (typeof PerformanceObserver === "undefined") {
    return;
  }

  try {
    const paintObserver = new PerformanceObserver((entryList) => {
      entryList.getEntries().forEach((entry) => {
        if (entry.name === "first-contentful-paint") {
          reportPerformanceMetric("nav_fcp_ms", entry.startTime, {
            navigation_kind: navigationKind,
          });
        }
      });
    });
    paintObserver.observe({ type: "paint", buffered: true });
  } catch (_error) {}

  try {
    const lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      latestLcpEntry = entries[entries.length - 1] || latestLcpEntry;
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
  } catch (_error) {}

  try {
    const longTaskObserver = new PerformanceObserver((entryList) => {
      longTaskCount += entryList.getEntries().length;
    });
    longTaskObserver.observe({ type: "longtask", buffered: true });
  } catch (_error) {}
};

const handlePageReady = () => {
  reportPerformanceMetric("page_ready_ms", monotonicNow() - navigationStartedAt, {
    navigation_kind: navigationKind,
  });

  if (navigationKind === "initial") {
    const flushRumMetricsWhenReady = () => {
      setTimeout(flushRumMetrics, 0);
    };

    if (document.readyState === "complete") {
      flushRumMetricsWhenReady();
    } else {
      window.addEventListener("load", flushRumMetricsWhenReady, { once: true });
    }

    navigationStartedAt = monotonicNow();
  }
};

export const __resetPerformanceMetricsForTests = () => {
  initialized = false;
  navigationStartedAt = 0;
  navigationKind = "initial";
  latestLcpEntry = null;
  longTaskCount = 0;
  rumMetricsFlushed = false;
};

export const initializePerformanceObservers = () => {
  if (initialized || typeof document === "undefined") {
    return;
  }

  initialized = true;
  registerObservers();

  document.addEventListener("turbo:visit", () => {
    navigationStartedAt = monotonicNow();
    navigationKind = "turbo";
  });

  document.addEventListener("turbo:load", handlePageReady);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushRumMetrics();
    }
  });

  window.addEventListener("pagehide", flushRumMetrics);
};
